import type { SkinMetricKey } from '../../models/RecommendationLab';

export const METRICS: Array<{ key: SkinMetricKey; label: string }> = [
  { key: 'breakouts', label: 'Breakouts' },
  { key: 'pores', label: 'Pores' },
  { key: 'redness', label: 'Redness' },
  { key: 'pigmentation', label: 'Pigmentation' },
  { key: 'lines', label: 'Lines' },
  { key: 'sagging', label: 'Sagging' },
  { key: 'uniformness', label: 'Uniformness' },
  { key: 'dark_circles', label: 'Dark Circles' },
  { key: 'puffiness', label: 'Puffiness' },
];

export interface LabPreset {
  name: string;
  skinType: string;
  metricScores: Record<SkinMetricKey, number>;
  surveySelections: Array<{
    questionId: string;
    values: string[];
  }>;
}

export const DEFAULT_PRESET: LabPreset = {
  name: 'Sumin — sensitive combo, pores',
  skinType: 'combination',
  metricScores: {
    breakouts: 62,
    pores: 41,
    redness: 48,
    pigmentation: 71,
    lines: 84,
    sagging: 78,
    uniformness: 66,
    dark_circles: 73,
    puffiness: 80,
  },
  surveySelections: [
    { questionId: 'age', values: ['25-34'] },
    { questionId: 'gender', values: ['female'] },
    { questionId: 'sensitive', values: ['sensitive'] },
    { questionId: 'skin_barrier_signs', values: ['tightness-after-cleansing'] },
    { questionId: 'skintype', values: ['combination'] },
    { questionId: 'sun_reaction', values: ['burns-barely-tans'] },
    { questionId: 'concerns', values: ['pores', 'redness', 'dark_circles'] },
    { questionId: 'concern_priority', values: ['pores'] },
    { questionId: 'budget', values: ['50'] },
    { questionId: 'factors', values: ['fragrance-free'] },
    { questionId: 'allergy', values: ['fragrance'] },
    { questionId: 'special', values: ['none'] },
    { questionId: 'lineup', values: ['moisturizer', 'sunscreen'] },
    { questionId: 'makeup', values: ['sometimes'] },
    { questionId: 'spf', values: ['sometimes'] },
  ],
};

export const PRESETS: LabPreset[] = [
  DEFAULT_PRESET,
  {
    name: 'Dry & lines focus',
    skinType: 'dry',
    metricScores: {
      breakouts: 82,
      pores: 74,
      redness: 68,
      pigmentation: 64,
      lines: 46,
      sagging: 55,
      uniformness: 58,
      dark_circles: 62,
      puffiness: 70,
    },
    surveySelections: [
      { questionId: 'age', values: ['35-44'] },
      { questionId: 'gender', values: ['female'] },
      { questionId: 'sensitive', values: ['non-sensitive'] },
      { questionId: 'skin_barrier_signs', values: ['tightness-after-cleansing', 'visible-flaking'] },
      { questionId: 'skintype', values: ['dry'] },
      { questionId: 'sun_reaction', values: ['burns-then-tans'] },
      { questionId: 'concerns', values: ['lines', 'dark_circles', 'dryness'] },
      { questionId: 'concern_priority', values: ['lines'] },
      { questionId: 'budget', values: ['100'] },
      { questionId: 'factors', values: ['none'] },
      { questionId: 'allergy', values: ['none'] },
      { questionId: 'special', values: ['none'] },
      { questionId: 'lineup', values: ['moisturizer', 'eye-cream'] },
      { questionId: 'makeup', values: ['sometimes'] },
      { questionId: 'spf', values: ['always'] },
    ],
  },
  {
    name: 'Oily breakouts',
    skinType: 'oily',
    metricScores: {
      breakouts: 38,
      pores: 52,
      redness: 60,
      pigmentation: 57,
      lines: 88,
      sagging: 82,
      uniformness: 61,
      dark_circles: 75,
      puffiness: 78,
    },
    surveySelections: [
      { questionId: 'age', values: ['under-25'] },
      { questionId: 'gender', values: ['male'] },
      { questionId: 'sensitive', values: ['non-sensitive'] },
      { questionId: 'skin_barrier_signs', values: ['none'] },
      { questionId: 'skintype', values: ['oily'] },
      { questionId: 'sun_reaction', values: ['tans-rarely-burns'] },
      { questionId: 'concerns', values: ['breakouts', 'pores', 'oiliness'] },
      { questionId: 'concern_priority', values: ['breakouts'] },
      { questionId: 'budget', values: ['30'] },
      { questionId: 'factors', values: ['non-comedogenic'] },
      { questionId: 'allergy', values: ['none'] },
      { questionId: 'special', values: ['none'] },
      { questionId: 'lineup', values: ['cleanser', 'toner'] },
      { questionId: 'makeup', values: ['rarely'] },
      { questionId: 'spf', values: ['sunny-only'] },
    ],
  },
  {
    name: 'Photosensitive & pigmentation',
    skinType: 'normal',
    metricScores: {
      breakouts: 78,
      pores: 73,
      redness: 45,
      pigmentation: 38,
      lines: 64,
      sagging: 69,
      uniformness: 52,
      dark_circles: 70,
      puffiness: 76,
    },
    surveySelections: [
      { questionId: 'age', values: ['45-54'] },
      { questionId: 'gender', values: ['female'] },
      { questionId: 'sensitive', values: ['sensitive'] },
      { questionId: 'skin_barrier_signs', values: ['stinging-burning'] },
      { questionId: 'skintype', values: ['normal'] },
      { questionId: 'sun_reaction', values: ['burns-never-tans'] },
      { questionId: 'concerns', values: ['pigmentation', 'redness', 'dullness'] },
      { questionId: 'concern_priority', values: ['pigmentation'] },
      { questionId: 'budget', values: ['100'] },
      { questionId: 'factors', values: ['fragrance-free'] },
      { questionId: 'allergy', values: ['none'] },
      { questionId: 'special', values: ['none'] },
      { questionId: 'lineup', values: ['sunscreen'] },
      { questionId: 'makeup', values: ['sometimes'] },
      { questionId: 'spf', values: ['always'] },
    ],
  },
];

export function scoreVariant(score: number): string {
  if (score < 60) {
    return 'danger';
  }
  if (score <= 75) {
    return 'warning';
  }
  return 'success';
}
